import { Badge } from '@/components/ui/Badge'
import { formatRelativeTime } from '@/lib/formatTime'
import type { RecentOrientationEntry } from '@/lib/dashboardApi'

interface OrientationHistoryRowProps {
  entry: RecentOrientationEntry
}

type BadgeVariant = 'default' | 'success' | 'warning' | 'error' | 'info'

const GRAVITE_CONFIG: Record<string, { label: string; variant: BadgeVariant }> = {
  GRAVE:   { label: 'Grave',   variant: 'error' },
  URGENT:  { label: 'Urgent',  variant: 'warning' },
  MODERE:  { label: 'Modéré',  variant: 'info' },
}

const STATUS_CONFIG: Record<string, { label: string; variant: BadgeVariant }> = {
  CONFIRMED:  { label: 'Confirmé',   variant: 'success' },
  PENDING:    { label: 'En attente', variant: 'default' },
  CANCELLED:  { label: 'Annulé',    variant: 'error' },
  SCORED:     { label: 'Évalué',    variant: 'info' },
}

function getGraviteConfig(gravite: string) {
  return GRAVITE_CONFIG[gravite] ?? { label: gravite, variant: 'default' as BadgeVariant }
}

function getStatusConfig(status: string) {
  return STATUS_CONFIG[status] ?? { label: status, variant: 'default' as BadgeVariant }
}

export function OrientationHistoryRow({ entry }: OrientationHistoryRowProps) {
  const graviteConf = getGraviteConfig(entry.gravite)
  const statusConf  = getStatusConfig(entry.status)

  return (
    <tr className="border-b border-neutral-100 last:border-b-0 hover:bg-neutral-50 transition-colors duration-100">
      {/* Référence */}
      <td className="px-4 py-3 text-xs font-mono text-neutral-500 whitespace-nowrap">
        {entry.orientationRef}
      </td>

      {/* Motif */}
      <td className="px-4 py-3 text-sm text-neutral-800 max-w-[180px] truncate">
        {entry.motif}
      </td>

      {/* Gravité */}
      <td className="px-4 py-3 whitespace-nowrap">
        <Badge variant={graviteConf.variant} size="sm">
          {graviteConf.label}
        </Badge>
      </td>

      {/* Statut */}
      <td className="px-4 py-3 whitespace-nowrap">
        <Badge variant={statusConf.variant} size="sm">
          {statusConf.label}
        </Badge>
      </td>

      {/* Structure choisie */}
      <td className="px-4 py-3 text-sm text-neutral-700 max-w-[200px] truncate">
        {entry.chosenStructureName}
      </td>

      {/* Temps relatif */}
      <td className="px-4 py-3 text-xs text-neutral-400 whitespace-nowrap text-right">
        {formatRelativeTime(entry.createdAt)}
      </td>
    </tr>
  )
}
